import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Find a Provider", path: "/find-provider" },
  { name: "Services", path: "/services" },
  { name: "Locations", path: "/locations" },
  { name: "Careers", path: "/careers" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <div className="w-10 h-10 rounded-lg bg-[#14213D] flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-bold text-xl text-[#14213D]">
                Medical Access
              </span>
              <span className="block text-xs text-gray-500 -mt-1">Inc</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-[#00B4D8]"
                    : "text-[#14213D] hover:text-[#00B4D8]"
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:1-866-841-2149" className="flex items-center gap-2 text-sm text-[#14213D]">
              <Phone className="w-4 h-4" />
              <span className="font-medium">(866) 841-2149</span>
            </a>
            <Link to="/find-provider">
              <Button
                className="bg-[#00B4D8] text-white rounded-lg px-5 py-2 hover:bg-[#0096c7] transition-colors"
                data-testid="nav-find-provider-btn"
              >
                Find a Provider
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-[#14213D]" />
            ) : (
              <Menu className="w-6 h-6 text-[#14213D]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
          >
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-2 text-base font-medium ${
                    location.pathname === link.path
                      ? "text-[#00B4D8]"
                      : "text-[#14213D]"
                  }`}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/find-provider" onClick={() => setIsOpen(false)}>
                <Button
                  className="w-full bg-[#00B4D8] text-white rounded-lg py-3 mt-4"
                  data-testid="mobile-find-provider-btn"
                >
                  Find a Provider
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
